import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  Card,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  Loading,
  Modal,
  RelativeDateTime,
  TextAreaInput,
  useToast,
} from 'corso-admin-ui';
import {
  CrewMerchantApi,
  crewMerchantShippingClaimApi,
  isTruthy,
} from 'corso-types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';
import DescriptionList from '~/components/DescriptionList';
import { QuickSearchListItem } from '~/components/QuickSearchListItem';
import {
  MIN_SEARCH_TERM_LENGTH,
  useSearchByKind,
} from '~/components/useSearchByKind';
import { isErrorMessage } from '~/utils/errorResponse';
import { useShippingClaimManualReorder } from '../hooks/useShippingClaimManualRefund';
import { useShippingClaim } from '../ShippingClaimReviewProvider';

type OrderListItem = Extract<
  CrewMerchantApi.ResponseBody<'/:storeId/search', 'post'>,
  { kind: 'Order' }
>['data'][number];

const manualReorderSchema =
  crewMerchantShippingClaimApi[
    '/:storeId/shipping-protection/claim/:shippingClaimId/manual-reorder'
  ].post.request.body;

export function ManualReorderShippingClaim({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { originalStoreOrder } = useShippingClaim();
  const { mutate: manualReorder, isPending } = useShippingClaimManualReorder();

  const [debouncedSearch, setDebouncedSearch] = useDebounceValue('', 500);
  const [stagedOrder, setStagedOrder] = useState<OrderListItem>();
  // a reorder has to come from the claim's own store
  const { data, isLoading, isRefetching } = useSearchByKind(
    debouncedSearch,
    'Order',
    { storeOnly: true },
  );
  const isAwaitingResults = isLoading || isRefetching;
  // the search only runs once there is enough to match on
  const hasSearched = debouncedSearch.length >= MIN_SEARCH_TERM_LENGTH;
  // the original order is the one being claimed, so it can never be its own reorder
  const orders = (data?.kind === 'Order' ? data.data : []).filter(
    (order) => order.id !== originalStoreOrder.id,
  );

  const formId = 'manual-reorder-shipping-claim-form';
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(manualReorderSchema),
    defaultValues: { comment: '' },
  });

  const closeAndReset = () => {
    reset();
    setStagedOrder(undefined);
    setDebouncedSearch('');
    onClose();
  };

  const stageOrder = (order?: OrderListItem) => {
    setStagedOrder(order);
    if (order) setValue('storeOrderId', order.id, { shouldValidate: true });
  };

  return (
    <Modal
      title="Manual Reorder"
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={!stagedOrder || isPending}
            loading={isPending}
          >
            Link Reorder
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit((body) =>
            manualReorder(body, {
              onSuccess: closeAndReset,
              onError: (error) =>
                toast.show(
                  isErrorMessage(error)
                    ? error.message
                    : 'Could not link this reorder. Please try again.',
                ),
            }),
          )(e).catch((error: unknown) =>
            console.error('Failed to link a manual reorder', error),
          );
        }}
        className="flex flex-col gap-4"
      >
        <Alert
          variant="tip"
          heading="Links an existing Shopify order and closes the claim as Reordered. Nothing new is created in Shopify."
        />

        {stagedOrder ? (
          <Card>
            <Card.Line>
              <Card.Heading>Order {stagedOrder.orderNo}</Card.Heading>
              <Action variant="ghost" onClick={() => stageOrder(undefined)}>
                Remove
              </Action>
            </Card.Line>
            <DescriptionList
              descriptions={[
                stagedOrder.customerName && {
                  term: 'Customer',
                  details: stagedOrder.customerName,
                },
                { term: 'Email', details: stagedOrder.customerEmail },
                {
                  term: 'Created',
                  details: (
                    <RelativeDateTime dateTime={stagedOrder.createdOn} full />
                  ),
                },
              ].filter(isTruthy)}
            />
          </Card>
        ) : (
          // no need to filter, will instead show all results returned by API
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search order number or email"
              onValueChange={setDebouncedSearch}
            />
            <CommandList>
              {isAwaitingResults && (
                <CommandLoading>
                  <Loading />
                </CommandLoading>
              )}

              {hasSearched && !isAwaitingResults && (
                <CommandEmpty>
                  <p className="text-sm text-neutral-500">
                    {`No orders found for "${debouncedSearch}".`}
                  </p>
                </CommandEmpty>
              )}

              {!!orders.length && (
                <CommandGroup heading={<p className="text-xs">Orders</p>}>
                  {orders.map((order) => (
                    <CommandItem
                      key={`orders-${order.id}`}
                      value={`${order.id}`}
                      onSelect={() => stageOrder(order)}
                    >
                      <QuickSearchListItem
                        customerName={order.customerName ?? order.customerEmail}
                        createdOn={order.createdOn}
                        externalId={order.orderNo}
                        icon="order"
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        )}

        <TextAreaInput
          id="manual-reorder-shipping-claim-comment"
          label="Comment"
          details="This comment will be added to the timeline."
          rows={3}
          {...register('comment')}
          error={errors.comment?.message}
        />
      </form>
    </Modal>
  );
}
